import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Plus, MoreHorizontal, Pencil, Trash2, UserCog } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import { roleLabel } from '@/lib/format';
import { useAuth } from '@/context/AuthContext';

export default function Users() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delTarget, setDelTarget] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '', role: 'vendedor', telefone: '', comissao_percentual: 0, active: true });

  const load = () => { api.get('/users').then((r) => setItems(r.data)).catch(() => {}).finally(() => setLoading(false)); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm({ name: '', email: '', password: '', role: 'vendedor', telefone: '', comissao_percentual: 0, active: true }); setOpen(true); };
  const openEdit = (u) => { setEditing(u); setForm({ ...u, password: '' }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast.error('Nome e email obrigatórios'); return; }
    setSaving(true);
    try {
      if (editing) {
        const payload = { name: form.name, telefone: form.telefone, role: form.role, comissao_percentual: Number(form.comissao_percentual || 0), active: form.active };
        if (form.password) payload.password = form.password;
        await api.put(`/users/${editing.user_id}`, payload); toast.success('Usuário atualizado');
      } else {
        if (form.password.length < 6) { toast.error('Senha mínima de 6 caracteres'); setSaving(false); return; }
        await api.post('/users', { name: form.name, email: form.email, password: form.password, role: form.role, telefone: form.telefone, comissao_percentual: Number(form.comissao_percentual || 0) });
        toast.success('Usuário criado');
      }
      setOpen(false); load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao salvar'); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    try { await api.delete(`/users/${delTarget.user_id}`); toast.success('Usuário removido'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Erro ao remover'); }
    finally { setDelTarget(null); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-semibold">Usuários</h2>
          <p className="text-sm text-muted-foreground">Vendedores, supervisores e administradores.</p>
        </div>
        <Button onClick={openNew} data-testid="users-new-button"><Plus className="h-4 w-4 mr-2" /> Novo usuário</Button>
      </div>

      <Card className="p-4">
        {loading ? <p className="text-muted-foreground py-8 text-center">Carregando...</p> : items.length === 0 ? (
          <div className="py-12 text-center"><UserCog className="h-8 w-8 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Nenhum usuário.</p></div>
        ) : (
          <Table data-testid="users-table">
            <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Perfil</TableHead><TableHead className="text-right">Comissão</TableHead><TableHead>Status</TableHead><TableHead className="w-10"></TableHead></TableRow></TableHeader>
            <TableBody>
              {items.map((u) => (
                <TableRow key={u.user_id}>
                  <TableCell className="font-medium">{u.name}</TableCell>
                  <TableCell className="text-muted-foreground">{u.email}</TableCell>
                  <TableCell><Badge variant="secondary">{roleLabel(u.role)}</Badge></TableCell>
                  <TableCell className="text-right tabular-nums">{u.comissao_percentual || 0}%</TableCell>
                  <TableCell>{u.active ? <Badge className="bg-[hsl(174_55%_34%/0.15)] text-[hsl(174_55%_24%)] hover:bg-[hsl(174_55%_34%/0.15)]">Ativo</Badge> : <Badge variant="secondary">Inativo</Badge>}</TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild><Button variant="ghost" size="icon" data-testid={`users-row-actions-${u.user_id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEdit(u)}><Pencil className="h-4 w-4 mr-2" /> Editar</DropdownMenuItem>
                        {isAdmin && u.user_id !== user.user_id && <DropdownMenuItem onClick={() => setDelTarget(u)} className="text-destructive"><Trash2 className="h-4 w-4 mr-2" /> Excluir</DropdownMenuItem>}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? 'Editar usuário' : 'Novo usuário'}</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Nome *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="user-name" /></div>
            <div><Label>Email *</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} disabled={!!editing} className="mt-1.5" data-testid="user-email" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Perfil</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger className="mt-1.5" data-testid="user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vendedor">Vendedor</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    {isAdmin && <SelectItem value="admin">Administrador</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Comissão (%)</Label><Input type="number" step="0.01" value={form.comissao_percentual} onChange={(e) => setForm({ ...form, comissao_percentual: e.target.value })} className="mt-1.5" /></div>
            </div>
            <div><Label>Telefone</Label><Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} className="mt-1.5" /></div>
            <div><Label>{editing ? 'Nova senha (opcional)' : 'Senha *'}</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? 'Deixe em branco para manter' : 'Mínimo 6 caracteres'} className="mt-1.5" data-testid="user-password" /></div>
            {editing && (
              <div className="flex items-center justify-between rounded-lg border p-3"><Label>Usuário ativo</Label><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /></div>
            )}
            <DialogFooter><Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancelar</Button><Button type="submit" disabled={saving} data-testid="user-save">{saving ? 'Salvando...' : 'Salvar'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!delTarget} onOpenChange={(o) => !o && setDelTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Excluir usuário?</AlertDialogTitle><AlertDialogDescription>Deseja excluir {delTarget?.name}?</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={doDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Excluir</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
